/**
 * Hooks de DASHBOARDS AP/AR e BAIXAS (write-off) — espelham os DTOs reais
 * de backend/apps/api/src/features/finance/presentation/http/dto/treasury.dto.ts.
 *
 * Regra do code-loop: nunca inventar campo. Cada interface abaixo é o espelho
 * campo a campo do DTO correspondente.
 *
 * Contratos consumidos (served-routes.json):
 *   GET  /finance/payables/dashboard        → PayableDashboardDto
 *   POST /finance/payables/:id/write-off    → WriteOffPayableResultDto (WriteOffPayableDto)
 *   GET  /finance/receivables/dashboard     → ReceivableDashboardDto
 *   POST /finance/receivables/:id/write-off → WriteOffReceivableResultDto (WriteOffReceivableDto)
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

/* ── Dashboards ── */

/** treasury.dto.ts: CashFlowDayDto */
export interface CashFlowDay {
  date: string;
  inflow: number;
  outflow: number;
  net: number;
  projectedBalance: number;
}

/** treasury.dto.ts: UpcomingPayableItemDto */
export interface UpcomingPayableItem {
  id: string;
  supplierName: string;
  documentNumber: string;
  dueDate: string;
  value: number;
  balance: number;
  status: string;
  category: string;
  daysToDue: number;
}

export type CashAlertSeverity = 'CRITICAL' | 'WARNING' | 'OK';

/** treasury.dto.ts: CashAlertDto */
export interface CashAlert {
  severity: CashAlertSeverity;
  message: string;
  value: number;
}

/** treasury.dto.ts: PayableDashboardDto */
export interface PayableDashboard {
  cashFlow30d: CashFlowDay[];
  projectedTotalInflow: number;
  projectedTotalOutflow: number;
  projectedNet: number;
  upcoming: UpcomingPayableItem[];
  alerts: CashAlert[];
  summary: {
    totalOpen: number;
    totalOpenCount: number;
    overdueTotal: number;
    overdueCount: number;
    dueNext7Days: number;
    dueNext7DaysCount: number;
    currentCash: number;
  };
}

/** treasury.dto.ts: AgingBucketDto */
export interface AgingBucket {
  label: string;
  value: number;
  count: number;
}

/** treasury.dto.ts: UpcomingInvoiceItemDto */
export interface UpcomingInvoiceItem {
  id: string;
  customerName: string;
  documentNumber: string;
  dueDate: string;
  value: number;
  balance: number;
  status: string;
  daysToDue: number;
}

/** treasury.dto.ts: ReceivableDashboardDto */
export interface ReceivableDashboard {
  aging: {
    current: number;
    days30: number;
    days60: number;
    days90: number;
    days120plus: number;
    total: number;
  };
  agingBuckets: AgingBucket[];
  upcoming: UpcomingInvoiceItem[];
  summary: {
    totalOpen: number;
    totalOpenCount: number;
    overdueTotal: number;
    overdueCount: number;
    dueNext7Days: number;
    dueNext7DaysCount: number;
    totalReceived30d: number;
  };
}

/* ── Baixa de títulos (write-off) ── */

/** treasury.dto.ts: WriteOffCompositionDto */
export interface WriteOffComposition {
  principal: number;
  interest: number;
  fine: number;
  discount: number;
  total: number;
}

/** treasury.dto.ts: AccountingPreviewEntryDto */
export interface AccountingPreviewEntry {
  type: 'DEBIT' | 'CREDIT';
  accountCode: string;
  accountName: string;
  value: number;
  description: string;
}

/** treasury.dto.ts: AccountingPreviewDto */
export interface AccountingPreview {
  entries: AccountingPreviewEntry[];
  balanced: boolean;
  totalDebit: number;
  totalCredit: number;
}

/** treasury.dto.ts: WriteOffPayableResultDto */
export interface WriteOffPayableResult {
  payableId: string;
  newBalance: number;
  newStatus: string;
  composition: WriteOffComposition;
  accountingPreview: AccountingPreview;
  transactionId: string;
}

/** treasury.dto.ts: WriteOffReceivableResultDto */
export interface WriteOffReceivableResult {
  receivableId: string;
  newBalance: number;
  newStatus: string;
  composition: WriteOffComposition;
  accountingPreview: AccountingPreview;
  transactionId: string;
}

/** treasury.dto.ts: WriteOffPayableDto (corpo do POST /finance/payables/:id/write-off) */
export interface WriteOffPayableInput {
  principal: number;
  interest?: number;
  fine?: number;
  discount?: number;
  paymentDate: string;
  paymentMethod: string;
  accountId: string;
  notes?: string;
}

/** treasury.dto.ts: WriteOffReceivableDto (corpo do POST /finance/receivables/:id/write-off) */
export interface WriteOffReceivableInput {
  principal: number;
  interest?: number;
  fine?: number;
  discount?: number;
  receivedDate: string;
  paymentMethod: string;
  accountId: string;
  notes?: string;
  salesPersonId?: string;
}

/* ── Query keys locais (padrão do treasuryHooks: nada toca query-keys.ts) ── */

const treasuryDashKeys = {
  payableDashboard: () => ['finance', 'treasury', 'payables-dashboard'] as const,
  receivableDashboard: () => ['finance', 'treasury', 'receivables-dashboard'] as const,
  payables: () => ['finance', 'payables'] as const,
  receivables: () => ['finance', 'receivables'] as const,
};

/* ── Dashboard AP ── */

export function usePayableDashboard() {
  return useQuery({
    queryKey: treasuryDashKeys.payableDashboard(),
    queryFn: () => apiClient.get<PayableDashboard>('/finance/payables/dashboard'),
  });
}

/* ── Dashboard AR ── */

export function useReceivableDashboard() {
  return useQuery({
    queryKey: treasuryDashKeys.receivableDashboard(),
    queryFn: () => apiClient.get<ReceivableDashboard>('/finance/receivables/dashboard'),
  });
}

/* ── Baixa AP ── */

export function useWriteOffPayable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: WriteOffPayableInput & { id: string }) =>
      apiClient.post<WriteOffPayableResult>(`/finance/payables/${id}/write-off`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: treasuryDashKeys.payableDashboard() });
      qc.invalidateQueries({ queryKey: treasuryDashKeys.payables() });
    },
  });
}

/* ── Baixa AR ── */

export function useWriteOffReceivable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: WriteOffReceivableInput & { id: string }) =>
      apiClient.post<WriteOffReceivableResult>(`/finance/receivables/${id}/write-off`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: treasuryDashKeys.receivableDashboard() });
      qc.invalidateQueries({ queryKey: treasuryDashKeys.receivables() });
    },
  });
}

/* ── Rótulos de apresentação (derivados, para exibição) ── */

export const CASH_ALERT_SEVERITY_LABEL: Record<CashAlertSeverity, string> = {
  CRITICAL: 'Crítico',
  WARNING: 'Atenção',
  OK: 'Ok',
};

export function alertVariant(severity: CashAlertSeverity): 'error' | 'warning' | 'success' {
  if (severity === 'CRITICAL') return 'error';
  if (severity === 'WARNING') return 'warning';
  return 'success';
}